import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { GoogleReCaptchaProvider, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import emailMask from 'text-mask-addons/dist/emailMask';
import { toast } from 'react-toastify';
import {
  Container,
  Row,
  Form,
  Col,
  Card,
  CardHeader,
  CardBody,
} from 'reactstrap';

import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button } from 'components/Button';

import { useConfig } from 'config/ConfigContext';
import { validateRegistration } from 'actions/dashboard';
import { numberUnmask } from 'utils/masks';
import { startAuthFlow } from 'utils/aws';
import { useNavigate } from 'react-router-dom';
import schema from './registrationSchema';

// Bound the BE identity pre-check to 30 seconds (Req 1.1, 1.2).
const IDENTITY_CHECK_TIMEOUT_MS = 30000;
const RECAPTCHA_POLL_MS = 200;

const Registration = (props) => {
  const {
    validator: {
      values,
      errors,
    },
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  // The provider mounts with this page, so executeRecaptcha is missing until
  // the script is ready. Keep the latest function so a submit that starts
  // early can wait for it instead of reading a stale render.
  const executeRecaptchaRef = useRef(props.googleReCaptchaProps.executeRecaptcha);
  executeRecaptchaRef.current = props.googleReCaptchaProps.executeRecaptcha;

  const handleInputChange = (event) => {
    const {
      validator: { onChangeHandler },
    } = props;

    const value = event.target.value.trim();
    onChangeHandler(event.target.name, value);
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const {
      validator: { validate },
    } = props;

    setIsLoading(true);

    if (validate(schema).isValid) {
      const emailLowercase = `${values.email}`.toLowerCase();
      const ssnUnmasked = numberUnmask(values.ssn);

      // Guards against the identity pre-check resolving after the 30s bound
      // has already fired (Req 1.1, 1.2). Whichever settles first wins; the
      // other path becomes a no-op.
      let settled = false;
      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        // Timeout: stay on the screen, retain entered SSN/email, show an error.
        toast.error('Identity verification could not be completed. Please try again.');
        setIsLoading(false);
      }, IDENTITY_CHECK_TIMEOUT_MS);

      // Wait out the script load, then request the token. A synchronous throw
      // means grecaptcha is not usable yet, so keep polling inside the same
      // 30s bound instead of failing the submit.
      const recaptchaPromise = new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let pollId;

        const attempt = () => {
          if (settled || Date.now() - startedAt >= IDENTITY_CHECK_TIMEOUT_MS) {
            clearTimeout(pollId);
            reject(new Error('reCAPTCHA is not ready'));
            return;
          }

          const executeRecaptcha = executeRecaptchaRef.current;
          if (typeof executeRecaptcha !== 'function') {
            pollId = setTimeout(attempt, RECAPTCHA_POLL_MS);
            return;
          }

          try {
            Promise.resolve(executeRecaptcha('user_registration')).then(
              (token) => {
                clearTimeout(pollId);
                resolve(token);
              },
              (err) => {
                clearTimeout(pollId);
                reject(err);
              }
            );
          } catch {
            pollId = setTimeout(attempt, RECAPTCHA_POLL_MS);
          }
        };

        attempt();
      });

      recaptchaPromise
        .then((token) => {
          if (settled) return;
          props.validateRegistration({
            data: {
              ...values,
              email: emailLowercase,
              ssn: ssnUnmasked,
              token,
            },

            success: (res) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              // The user has already completed registration. Do not continue to
              // Cognito credential creation; tell them to sign in (or reset
              // their password from the Sign-In page) and route to sign-in.
              if (res?.code === 'RegistrationCompleted') {
                toast.info('User already registered. You can proceed to login. If you do not remember the password, you can reset your password from the Sign-In page.');
                navigate('/');
                setIsLoading(false);
                return;
              }
              // The user already registered but is still awaiting email
              // confirmation (e.g. they lost the verification-code screen and
              // started registration again). Send them back to the existing
              // Enter Verification Code screen instead of continuing to Cognito
              // credential creation. All other responses keep prior behavior.
              if (res?.code === 'PendingConfirmation') {
                // The backend returns the Cognito username (accessUserName) for
                // the pending user; the verify screen needs it as `userName` to
                // confirm the emailed code. No password is available on this
                // path, so the verify screen's no-password branch routes to the
                // sign-in success page after confirming (it does not attempt an
                // auto sign-in).
                // Entering the email-verification step of registration: mark
                // the auth flow so the two-factor route guard allows the screen.
                startAuthFlow();
                navigate('/two-factor/verify-email', {
                  state: {
                    userName: res.accessUserName,
                    ssn: ssnUnmasked,
                    email: emailLowercase,
                  },
                });
                setIsLoading(false);
                return;
              }
              // Eligible identity: always proceed to Cognito credential creation
              // carrying { ssn, email } in navigation state (Req 1.4, 10.3).
              navigate('/registration-confirm', {
                state: {
                  ssn: ssnUnmasked,
                  email: emailLowercase,
                },
              });
              setIsLoading(false);
            },

            fail: (err) => {
              if (settled) return;
              settled = true;
              clearTimeout(timeoutId);
              // The backend may report an already-completed registration as an
              // error. In that case tell the user to sign in (or reset their
              // password from the Sign-In page) and route to sign-in.
              if (err?.data?.code === 'RegistrationCompleted') {
                toast.info('User already registered. You can proceed to login. If you do not remember the password, you can reset your password from the Sign-In page.');
                navigate('/');
                setIsLoading(false);
                return;
              }
              // The backend may report an already-registered-but-unconfirmed
              // user as an error. In that case route back to the existing Enter
              // Verification Code screen rather than surfacing the error.
              if (err?.data?.code === 'PendingConfirmation') {
                // Existing unconfirmed user: re-enter the email-verification
                // step; mark the auth flow for the two-factor route guard.
                startAuthFlow();
                navigate('/two-factor/verify-email', {
                  state: {
                    userName: err.data.accessUserName,
                    ssn: ssnUnmasked,
                    email: emailLowercase,
                  },
                });
                setIsLoading(false);
                return;
              }
              // Not eligible / BE error: stay on the screen, retain inputs.
              const msg = err?.data?.message || 'Try again';
              toast.error(msg);
              setIsLoading(false);
            },
          });
        })
        .catch(() => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          // reCAPTCHA failure (Req 1.2): stay on the screen, retain inputs.
          toast.error('Identity verification could not be completed. Please try again.');
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <Container fluid>
        <Form onSubmit={handleSubmitFrom}>
          <Row className="align-items-center" style={{ minHeight: '500px' }}>
            <Col lg={{ size: 4, offset: 4 }} md={{ size: 6, offset: 3 }}>
              <Card>
                <CardHeader>User Registration</CardHeader>
                <CardBody>
                  <Row>
                    <Col>
                      <h5 className="mb-2 text-center">
                        Enter the information below to verify your identity
                      </h5>
                    </Col>
                  </Row>

                  <Row className="pt-3">
                    <Col>
                      <Input
                        name="ssn"
                        label="SSN"
                        value={values.ssn}
                        onChange={handleInputChange}
                        hasError={!!errors.ssn}
                        hasValue
                        isMasked={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        placeHolder="Type SSN"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.ssn}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Input
                        name="email"
                        label="Email"
                        isMasked={emailMask}
                        value={values.email}
                        onChange={handleInputChange}
                        hasError={!!errors.email}
                        hasValue
                        placeHolder="Email"
                        isBadgeVisible={false}
                        isRequired
                        errorMessage={errors.email}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Button
                        className="w-100"
                        onClick={handleSubmitFrom}
                        isLoading={isLoading}
                        color="primary"
                        type="submit"
                      >
                        {'Next >>'}
                      </Button>
                    </Col>
                  </Row>

                  <Row>
                    <Col style={{ fontSize: 12, color: '#888' }}>
                      This site is protected by reCAPTCHA and the Google&nbsp;
                      <a href="https://policies.google.com/privacy">Privacy Policy</a>
                      {' '}
                      and&nbsp;
                      <a href="https://policies.google.com/terms">Terms of Service</a>
                      &nbsp;also apply.
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

Registration.propTypes = {
  validator: PropTypes.object.isRequired,
  googleReCaptchaProps: PropTypes.object.isRequired,
  validateRegistration: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  validateRegistration,
};

const RegistrationForm = compose(
  Validator(schema),
  connect(null, mapDispatchToProps),
  withGoogleReCaptcha
)(Registration);

const RegistrationPage = () => {
  const { reCaptchaKey } = useConfig();

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={reCaptchaKey}
      scriptProps={{ id: 'google-recaptcha-v3-registration' }}
    >
      <RegistrationForm />
    </GoogleReCaptchaProvider>
  );
};

export default RegistrationPage;
